import {
  ALL_CHART_TYPES,
  BLACK_HEX,
  DEFAULT_LINE_THICKNESS,
  DEFAULT_NORMALIZE_BY,
  DEFAULT_NORMALIZE_BY_SETTING
} from "constants/chart.constants";
import { CalculationMethod, EvaSource, TimeStep } from "constants/charts.enums";
import { createMergeWithDefaults } from "entities/utils";
import { BoxPlotChartRequestType, BoxPlotChartSettings } from "types";

import { MaxBinsSortOrderEnum } from "models/binSize";
import { EntityKind } from "models/entityKind";
import { PdenSourceEnum } from "models/pdenDataSourceSetting";

import { BASE_CHART_REQUEST } from "./defaults";

export const DEFAULT_BOX_PLOT_MIDSTREAM_CHART_SETTINGS: Partial<BoxPlotChartSettings> = {
  product: "Gas Inlet",
  chartType: ALL_CHART_TYPES.BoxPlot.id,
  otherProducts: [],
  showForecast: true,
  averageSettings: {
    color: BLACK_HEX,
    thickness: DEFAULT_LINE_THICKNESS
  },
  showAverage: false,
  p10Settings: {
    color: BLACK_HEX,
    thickness: DEFAULT_LINE_THICKNESS
  },
  showP10: false,
  p50Settings: {
    color: BLACK_HEX,
    thickness: DEFAULT_LINE_THICKNESS
  },
  showP50: false,
  p90Settings: {
    color: BLACK_HEX,
    thickness: DEFAULT_LINE_THICKNESS
  },
  showP90: false,
  pdenDataSourceSetting: {
    source: EvaSource.Public,
    timeStep: TimeStep.Month
  },
  forecastMode: "forecastTrump",
  useForecastStartDate: false,
  survivorBias: true,
  rateType: 0,
  showIndividualSeries: false,
  cutoff: 50,
  timeStep: TimeStep.Month,
  shutInMonths: 3,
  source: EvaSource.Public,
  normalizeBy: DEFAULT_NORMALIZE_BY,
  useWeightedRatioAverage: true,
  xVariable: {
    valueProperty: "Header.FacilityStartDate_YearMonth",
    displayTitle: "Start Date (Year-Month)",
    canBin: false,
    dataType: "Text",
    mappingName: "Header.FacilityStartDate_YearMonth",
    bin: null,
    pdenSource: PdenSourceEnum.Public.toString()
  },
  yVariable: {
    valueProperty: "Performance.GasInlet.CurrentCalRate",
    displayTitle: "Current Cal. Rate (Mcf/d)",
    canBin: true,
    dataType: "Number",
    mappingName: "Performance.GasInlet.CurrentCalRate",
    pdenSource: PdenSourceEnum.Public.toString()
  },
  lockUnits: false,
  stackedBarCalculation: CalculationMethod.Count,
  pressureChartSettings: {
    product: "Oil",
    productivityIndex: false
  },
  movingAverageDays: null,
  annotationSettings: {}
};

export const DEFAULT_BOX_PLOT_MIDSTREAM_CHART_REQUEST: Partial<BoxPlotChartRequestType> =
  {
    ...BASE_CHART_REQUEST,
    FilterId: "",
    requestId: "",
    ReverseColor: false,
    ColorPalette: {
      id: "-6",
      name: "Portland",
      shared: false,
      colors: ["rgb(12,51,131)"],
      preferredColors: [],
      paletteType: 0,
      colorScale: [],
      specialColors: null,
      thickness: [2],
      reverse: false
    },
    normalizeTypeWell: true,
    GroupBy: {
      title: "Facility Operator",
      property: "Header.OperatorName",
      groupByField: "Header.OperatorName",
      pdenSource: "Public",
      canBin: false,
      dataType: "Text", // TODO: check this
      categoryId: 1,
      tooltip: "",
      display: "",
      entityKind: EntityKind.Facility,
      bin: {
        BinSize: 0,
        MinSize: null,
        MaxBins: null,
        MaxBinsSortOrder: MaxBinsSortOrderEnum.WellCount,
        GreaterThan: null,
        LessThan: null,
        Quantile: {
          quantileType: "NumberOfQuantile",
          numQuantiles: 4
        },
        BinType: "BinSize",
        UseDynamicBins: false
      },
      mode: undefined,
      forecastTrumpingSettings: undefined,
      performanceDateRange: undefined
    },
    sortBy: "AlphabeticalAsc",
    userForecast: [],
    userForecastSettings: {
      forecastSourceFolder: null,
      excludeMcDanielForecasts: false,
      reserveCategory: null,
      excludeNoviForecasts: true,
      specialForecastFolders: ["McDaniel Research"]
    },
    forecastMode: "forecastTrump",
    useForecastStartDate: false,
    showGroupsNotInFilter: true,
    placeholderBinsEnabled: false,
    hasCheckedBins: true,
    syncClientPden: false,
    syncClientSchema: "",
    colorLockedItems: {},
    syncClientFcst: false,
    syncForecastRescat: "2111",
    normalizeBySetting: DEFAULT_NORMALIZE_BY_SETTING,
    chartSetting: DEFAULT_BOX_PLOT_MIDSTREAM_CHART_SETTINGS as BoxPlotChartSettings,
    chartFieldInformation: {
      xField: {
        type: "x",
        title: "Start Date (Year-Month)",
        property: "Header.FacilityStartDate_YearMonth",
        defaultUnit: "",
        product: "",
        performanceDateRange: undefined
      },
      yField: {
        type: "y",
        title: "Current Cal. Rate (Mcf/d)",
        property: "Performance.GasInlet.CurrentCalRate",
        defaultUnit: "Mcf",
        product: "Gas Inlet",
        performanceDateRange: undefined
      },
      groupByField: {
        type: "groupBy",
        title: "Facility Operator",
        property: "Header.OperatorName",
        performanceDateRange: undefined
      }
    },
    axisMinMax: {
      xMin: null,
      xMax: null,
      yMin: null,
      yMax: null
    },
    isDefaultLockOn: true,
    showScatter: false
  };

export const generateBoxPlotMidstreamRequestBody = createMergeWithDefaults(
  DEFAULT_BOX_PLOT_MIDSTREAM_CHART_REQUEST
);
